// Attempt count for when a request is stale due to a keep-alive socket reset
const MAX_ATTEMPTS = 2;

/**
 * Determines if a request was made using a keep-alive socket that was reset by the peer
 *
 * @param {object} error - SDK rejection `{ req, err }`
 * @returns {boolean}
 */
const isStaleKeepAliveReset = error => {
  const message = error?.err?.message || '';
  const isReset =
    message.includes('ECONNRESET') || message.includes('socket hang up');

  return Boolean(error?.req?.reusedSocket) && isReset;
};

/**
 * queryWithSearchSDK - a helper to query from the searchSDK without having to throw try/catch blocks all over
 * @param {object} opts
 * @param {function} opts.searchMethod - reference bound with arguments OR legacy reference (e.g. `LocalNames.countByState`)
 * @param {object} [opts.queryParams] - optional object passed to the searchMethod (legacy)
 * @param {any} [opts.defaultReturnValue] - the value the method should return if the query fails
 * @param {object} [$bugsnag] - an instance of the bugsnag error reporting service
 * @returns {Promise<*|*|*[]>}
 */
const queryWithSearchSDK = async (opts = {}, $bugsnag = null) => {
  const defaultReturnValue =
    opts.defaultReturnValue || opts.defaultReturnValue === 0
      ? opts.defaultReturnValue
      : [];

  // If required params don't exist, return default
  if (!opts || !opts.searchMethod) {
    return defaultReturnValue;
  }

  // If required params are wrong type, return default
  if (typeof opts.searchMethod !== 'function') {
    return defaultReturnValue;
  }
  const { searchMethod, queryParams } = opts;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      // If queryParams was provided (legacy callers), pass it through; otherwise the searchMethod is already bound with arguments.
      return queryParams !== undefined
        ? await searchMethod(queryParams)
        : await searchMethod();
    } catch (err) {
      if (isStaleKeepAliveReset(err) && attempt < MAX_ATTEMPTS) {
        continue;
      }

      let reqError;
      if (err?.err) {
        reqError = err.err?.message;
        if (err?.req?.path) {
          reqError += ` - ${err?.req?.path}`;
        }
      }
      const notifyError = reqError ? new Error(reqError) : err;
      if (typeof $bugsnag?.notify === 'function') {
        $bugsnag.notify(notifyError, event => {
          event.addMetadata('Extra Info', {
            page: 'shared/helpers/queryWithSearchSDK.js',
            method: 'queryWithSearchSDK',
            actualError: reqError || err?.message,
            reusedSocket: err?.req?.reusedSocket
          });
        });
      }
      return defaultReturnValue;
    }
  }

  return defaultReturnValue;
};

export default queryWithSearchSDK;
